import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';

import { FieldChrome } from './FieldChrome';

const cx = (...names: (string | false | undefined)[]): string => names.filter(Boolean).join(' ');

export const baseControlClasses =
  'w-full bg-zinc-50 dark:bg-zinc-900/80 text-zinc-900 dark:text-zinc-100 border border-zinc-200 dark:border-white/10 rounded-2xl px-4 py-2.5 text-sm transition-all duration-300 placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-zinc-900 dark:focus:border-brand-500 dark:backdrop-blur-md';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  optional?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, optional, id, className, ...rest },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const invalid = error !== undefined && error !== '';
  return (
    <FieldChrome label={label} hint={hint} error={error} optional={optional} id={fieldId}>
      <input
        ref={ref}
        id={fieldId}
        aria-invalid={invalid || undefined}
        className={cx(
          baseControlClasses,
          invalid
            ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-200 dark:border-rose-500/50 dark:focus:ring-rose-500/20'
            : 'focus:ring-zinc-900/10 dark:focus:ring-brand-500/20',
          className,
        )}
        {...rest}
      />
    </FieldChrome>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  optional?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, optional, id, className, ...rest },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const invalid = error !== undefined && error !== '';
  return (
    <FieldChrome label={label} hint={hint} error={error} optional={optional} id={fieldId}>
      <textarea
        ref={ref}
        id={fieldId}
        aria-invalid={invalid || undefined}
        className={cx(
          baseControlClasses,
          invalid
            ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-200 dark:border-rose-500/50 dark:focus:ring-rose-500/20'
            : 'focus:ring-zinc-900/10 dark:focus:ring-brand-500/20',
          className,
        )}
        {...rest}
      />
    </FieldChrome>
  );
});
